import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  OrderLinesFilterKeys,
  OrderLinesListFilterOpts
} from "../../components/TransportReportListPage/filters";
import { InitialTransportReportFilterData_malls_edges_node } from "../../types/InitialTransportReportFilterData";
import {
  transportReportListUrlFilters,
  TransportReportListUrlFiltersEnum,
  TransportReportListUrlFiltersWithMultipleValues,
  TransportReportUrlQueryParams
} from "../../urls";
export const SALESREPORTS_FILTERS_KEY = "OrderLinesFilters";

export function getFilterOpts(
  params: transportReportListUrlFilters,
  malls: {
    initial: InitialTransportReportFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): OrderLinesListFilterOpts {
  return {
    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderCourier: {
      active: !!params.orderCourier,
      value: maybe(() => dedupeFilter(params.orderCourier), null)
    },
    orderDate: {
      active: maybe(
        () =>
          [params.orderDateFrom, params.orderDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderDateTo, ""),
        min: maybe(() => params.orderDateFrom, "")
      }
    },
    shippingMethodName: {
      active: !!params.deliveryType,
      value: maybe(() => dedupeFilter(params.deliveryType), null)
    }
  };
}

export function getFilterVariables(params: transportReportListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );

  const endDate = moment(`${endYear}-${endMonth}-${endDay} 00:00:00`)
    .add(1, "days")
    .format("YYYY-MM-DD HH:mm:ssZZ");

  return {
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    orderCourier: maybe(() => params.orderCourier, null),
    orderDate: getGteLteVariables({
      gte: params.orderDateFrom
        ? moment(`${params.orderDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.orderDateFrom && params.orderCompleteDateFrom
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),

      lte: params.orderDateTo
        ? moment(`${params.orderDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.orderDateTo && params.orderCompleteDateTo
        ? null
        : endDate
    }),

    search: params.query,
    shippingMethodName: maybe(() => params.deliveryType, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<OrderLinesFilterKeys>,
  params: transportReportListUrlFilters
): transportReportListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case OrderLinesFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        TransportReportListUrlFiltersWithMultipleValues.mall
      );
    case OrderLinesFilterKeys.orderDate:
      return getMinMaxQueryParam(
        filter,
        TransportReportListUrlFiltersEnum.orderDateFrom,
        TransportReportListUrlFiltersEnum.orderDateTo
      );
    case OrderLinesFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        TransportReportListUrlFiltersEnum.orderCompleteDateFrom,
        TransportReportListUrlFiltersEnum.orderCompleteDateTo
      );
    case OrderLinesFilterKeys.shippingMethodName:
      return getMultipleValueQueryParam(
        filter,
        TransportReportListUrlFiltersWithMultipleValues.deliveryType
      );
    case OrderLinesFilterKeys.orderCourier:
      return getMultipleValueQueryParam(
        filter,
        TransportReportListUrlFiltersWithMultipleValues.orderCourier
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<transportReportListUrlFilters>(
  SALESREPORTS_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  TransportReportUrlQueryParams,
  transportReportListUrlFilters
>({
  ...TransportReportListUrlFiltersEnum,
  ...TransportReportListUrlFiltersWithMultipleValues
});
